export enum TreeViewSelectionState {
  None,
  Selected,
  ChildSelected,
  AllChildrenSelected
}

export abstract class TreeViewSelectionNode<TSourceType, TSourceKeyType> {
  public displayValue: string;
  public sourceValue: TSourceType;
  public sourceKey: TSourceKeyType;
  public parent: TreeViewSelectionNode<TSourceType, TSourceKeyType> | undefined;
  public children: TreeViewSelectionNode<TSourceType, TSourceKeyType>[] = [];
  public level: number = 1;

  // Config Values
  public expandable: boolean = false;
  public allowSelection: boolean = false;

  // State Values
  public isLoading: boolean = false;
  private isSelected: boolean = false;

  get selected(): boolean {
    return this.isSelected;
  }

  private get anyChildSelect() : boolean {
    if (this.children.length === 0) return false;

    let hasChildSelected = false;

    this.children.forEach(c => {
      if (c.isSelected) {
        hasChildSelected = true;
      }
      else if (c.children.length > 0) {
        let childrenChecked = c.anyChildSelect;
        if (childrenChecked) {
          hasChildSelected = true;
        }
      }
    });

    return hasChildSelected;
  }

  private get allChildrenSelected() : boolean {
    if (this.children.length === 0 && this.isSelected) return true;
    if (this.children.length === 0) return false;

    let allSelected = true;

    this.children.forEach(c => {
      if (!c.isSelected || (c.children.length > 0 && !c.allChildrenSelected)) {
        allSelected = false;
      }
    });

    return allSelected;
  }

  constructor(
    allKnownSelections: TSourceKeyType[],
    displayValue: string,
    level: number,
    sourceValue: TSourceType,
    parent: TreeViewSelectionNode<TSourceType, TSourceKeyType>,
    expandable: boolean = false,
    allowSelection: boolean = false
    ) {
    this.displayValue = displayValue;
    this.level = level;
    this.sourceValue = sourceValue;
    this.parent = parent;
    this.expandable = expandable;
    this.allowSelection = allowSelection;
    this.sourceKey = this.deriveKey();

    allKnownSelections.forEach(k => {
      if (this.isKeyForMe(k)) {
        this.setSelection(true);
      }
    });

    if (parent != null && parent.isSelected) {
      this.setSelection(true);
    }
  }

  public selectionState(allKnownSelections: TSourceKeyType[]) : TreeViewSelectionState {
    const anyChildSelected = this.anyChildSelect;
    const anyKnownChildSelected = this.isAnyKnownChildSelected(allKnownSelections);
    const allChildrenSelected = this.allChildrenSelected;

    let finalState = TreeViewSelectionState.None;

    if (allChildrenSelected) finalState = TreeViewSelectionState.AllChildrenSelected;
    else if (anyChildSelected || anyKnownChildSelected) finalState = TreeViewSelectionState.ChildSelected;
    else if (this.isSelected) finalState = TreeViewSelectionState.Selected;

    return finalState;
  }

  public toggleSelection() {
    this.setSelection(!this.isSelected);
  }

  public setSelection(isSelected: boolean) {
    this.isSelected = isSelected
    this.children.forEach(c => c.setSelection(isSelected));
  }

  protected abstract deriveKey() : TSourceKeyType;
  protected abstract isAnyKnownChildSelected(allKnownSelections: TSourceKeyType[]) : boolean;
  public abstract isKeyForMe(key: TSourceKeyType): boolean;
  public abstract isChildOf(key: TSourceKeyType): boolean;
  public abstract toSourceKey(): TSourceKeyType;
  public abstract matchesKey(key: TSourceKeyType): boolean;
}
